import AttachmentPreview from "@/business/notify/AttachmentPreview";
import { formatDate } from "%/date";
import { Button } from "@/Button";
import DateButton from "@/common/DateButton";
import MessageModal from "@/MessageModal";
import { Dialog } from "@/Dialog";
import ToggleSwitch from "@/common/ToggleSwitch";
import { ValidateSelectList } from "@/SelectList";
import { ValidateTextarea } from "@/Textarea";
import { Controller } from "react-hook-form";
import { CalendarNew as CalendarComponent } from "@/CalendarNew";
import { useRouter } from "@tanstack/react-router";
import useNotifySendSavedNotify from "^/business/notify/useNotifySendSavedNotify";
import {
  canUseNativePhotoPicker,
  openNativePhotoPicker,
} from "%/nativePhotoPicker";
import { expandRecipientValues } from "@/business/send/groupedRecipientOptions";
import { ValidateInput } from "@/Input";
import { BusinessNoticeTempDetailResponse } from "~/types/common";
import { ValidateSelectListMulti } from "@/SelectListMulti";
import {
  MAX_NOTICE_ATTACHMENTS,
  NOTICE_ATTACHMENT_LIMIT_MESSAGE,
} from "%/noticeAttachmentPolicy";
// 갤러리 폴백 더미는 두지 않는다.
// 조회 실패/0건일 때 임의의 사진을 노출하면 다른 사업장의 사진이
// 학부모에게 발송될 수 있으므로, 빈 상태 안내를 대신 보여준다.

const SendSavedNotify = ({
  data,
  editMode = false,
}: {
  data: BusinessNoticeTempDetailResponse["data"];
  editMode?: boolean;
}) => {
  const router = useRouter();
  const {
    // 폼 관련
    formMethods: {
      handleSubmit,
      control,
      register,
      watch,
      formState: { isValid, errors },
    },

    // 선택 목록 데이터
    selectListData,

    // 날짜 관련
    date,
    selectedDate,
    setSelectedDate,

    // 모달 관련
    calendarModal: { isOpenModal, clickModal, closeModal },
    imageModal: {
      isOpenModal: isOpenImageModal,
      clickModal: clickImageModal,
      closeModal: closeImageModal,
    },

    // 이미지 관련
    selectedImages,
    modalSelectedImages,
    toggleImageSelection,
    removeSelectedImage,
    handleImgModal,
    addBridgeImages,

    // 앨범 관련
    albumData,
    fetchAlbumList,

    // 토글 관련
    // isToggle,
    // setIsToggle,
    isTimeToggle,
    handleTimeToggleChange,

    // 다이얼로그 관련
    showSuccessDialog,
    setShowSuccessDialog,

    // 제출 관련
    bindSubmit,
    isSubmitting,
  } = useNotifySendSavedNotify(data, editMode);

  return (
    <>
      <form className="flex-col flex-auto gap-1 px-5 bg-[#F5F5F5] grid grid-cols-1 animate-fadeIn">
        <div className="mb-5 animate-slideInFromTop">
          <Controller
            name="recipient"
            control={control}
            rules={{ required: "필수 선택입니다." }}
            render={({ field }) => (
              <ValidateSelectListMulti
                onChange={(selectedValues) => {
                  field.onChange(selectedValues);
                }}
                label="수신"
                placeholder="반려견 이름 선택 (다중선택 가능)"
                options={
                  selectListData?.recipients?.map((recipient) => ({
                    value: recipient.petSeq.toString(),
                    label: recipient.petName,
                  })) || []
                }
                value={
                  Array.isArray(field.value)
                    ? field.value
                    : field.value
                      ? [field.value]
                      : []
                }
                errorMessage={errors?.recipient?.message}
              />
            )}
          />
          <div className="my-4">
            <ValidateInput
              className="w-full"
              label="제목"
              placeholder="제목을 입력하세요."
              errorMessage={errors?.notiTitle?.message}
              {...register("notiTitle", {
                required: { value: true, message: "제목을 입력하세요" },
              })}
            />
          </div>
          <div
            className="my-5 animate-slideInFromTop"
            style={{ animationDelay: "0.1s" }}
          >
            <div className="flex justify-between items-center mb-1">
              <p className="mb-2 text-sm font-medium">발송시간</p>
              <ToggleSwitch
                checked={isTimeToggle}
                onChange={handleTimeToggleChange}
              />
            </div>
            {isTimeToggle && (
              <div className="flex gap-1 items-center mb-1">
                <DateButton
                  value={selectedDate ? formatDate(selectedDate) : date}
                  onClick={() => clickModal()}
                />
                <Controller
                  name="hour"
                  control={control}
                  rules={{ required: "시간을 선택하세요" }}
                  render={({ field }) => {
                    const hourScalar =
                      typeof field.value === "string"
                        ? field.value
                        : (field.value as { value?: string })?.value ?? "";
                    return (
                      <ValidateSelectList
                        isDefaultValue={true}
                        placeholder="시"
                        isSearchable={false}
                        options={Array.from({ length: 24 }, (_, i) => ({
                          value: (i + 1).toString().padStart(2, "0"),
                          label: (i + 1).toString().padStart(2, "0"),
                        }))}
                        boxHeight={20}
                        className="h-10 text-lg text-center"
                        value={
                          hourScalar
                            ? { value: hourScalar, label: hourScalar }
                            : null
                        }
                        onChange={(option) =>
                          field.onChange(option?.value ?? "")
                        }
                      />
                    );
                  }}
                />
                <span className="text-[12px]">시</span>
                <Controller
                  name="minute"
                  control={control}
                  rules={{ required: "분을 선택하세요" }}
                  render={({ field }) => {
                    const minuteScalar =
                      typeof field.value === "string"
                        ? field.value
                        : (field.value as { value?: string })?.value ?? "";
                    return (
                      <ValidateSelectList
                        isDefaultValue={true}
                        placeholder="분"
                        isSearchable={false}
                        options={Array.from({ length: 6 }, (_, i) => ({
                          value: (i * 10).toString().padStart(2, "0"),
                          label: (i * 10).toString().padStart(2, "0"),
                        }))}
                        boxHeight={20}
                        className="h-10 text-lg text-center"
                        value={
                          minuteScalar
                            ? { value: minuteScalar, label: minuteScalar }
                            : null
                        }
                        onChange={(option) =>
                          field.onChange(option?.value ?? "")
                        }
                      />
                    );
                  }}
                />
                <span className="text-[12px]">분</span>
              </div>
            )}
          </div>
          <div className="my-5">
            <Controller
              name="content"
              control={control}
              rules={{ required: "필수 선택입니다." }}
              render={({ field }) => (
                <ValidateTextarea
                  {...field}
                  className="w-full h-100"
                  label="내용"
                  rows={10}
                />
              )}
            />
          </div>

          {/* 사진 등록 */}
          <div className="my-5">
            <p className="mb-2 text-sm font-medium">
              사진등록 ({selectedImages.length}/{MAX_NOTICE_ATTACHMENTS})
            </p>
            <div className="flex justify-between">
              <button
                className="w-full min-h-[36px] bg-white rounded-md border border-gray-400 transition-all duration-300 hover:bg-gray-50 hover:border-gray-500 transform hover:scale-[1.02]"
                onClick={async (e) => {
                  e.preventDefault();
                  // 수신자가 선택되었는지 확인 (다중 선택 값 "12,34" 도 개별 petSeq 로 전개)
                  const selectedRecipient = watch("recipient");
                  const petSeqs = Array.from(
                    expandRecipientValues(selectedRecipient)
                  );
                  if (petSeqs.length === 0) {
                    alert("먼저 수신자를 선택해주세요.");
                    return;
                  }
                  const remaining = MAX_NOTICE_ATTACHMENTS - selectedImages.length;
                  if (remaining <= 0) {
                    alert(NOTICE_ATTACHMENT_LIMIT_MESSAGE);
                    return;
                  }
                  // 앱(신버전) = 네이티브 통합 사진첩(다중). 반려견 1마리면 그 펫 클라우드 탭 포함,
                  // 여러 마리면 스마트폰만. 그 외(구버전 앱·브라우저) = 기존 웹 모달 (신규 발송과 동일)
                  if (canUseNativePhotoPicker()) {
                    try {
                      const picked = await openNativePhotoPicker({
                        petSeq:
                          petSeqs.length === 1 ? Number(petSeqs[0]) : undefined,
                        multiSelect: remaining,
                      });
                      if (picked.length) addBridgeImages(picked);
                    } catch (err) {
                      alert(
                        err instanceof Error
                          ? err.message
                          : "사진첩을 여는 데 실패했습니다."
                      );
                    }
                    return;
                  }
                  // 앨범 목록 가져오기 (기존 웹 모달)
                  fetchAlbumList(selectedRecipient);
                  clickImageModal();
                }}
              >
                <p>사진 불러오기</p>
              </button>
            </div>
            {/* 선택된 사진 미리보기 */}
            <div className="flex flex-col gap-2 overflow-y-auto max-h-[500px] mt-3">
              <div className="grid grid-cols-4 gap-1 sm:grid-cols-4">
                {selectedImages.length > 0 &&
                  selectedImages[0].ImgURL &&
                  selectedImages.map((data) => {
                    return (
                      <div
                        key={data.ImgID}
                        className="relative cursor-pointer aspect-square"
                      >
                        <AttachmentPreview
                          fileSeq={data.ImgID}
                          originalUrl={data.ImgURL}
                          thumbnailUrl={data.thumbnailUrl}
                          thumbnailStatus={data.thumbnailStatus}
                        />
                        {/* X(삭제) 버튼 */}
                        <button
                          type="button"
                          className="flex absolute top-1 right-1 z-10 justify-center items-center w-4 h-4 text-xs bg-white bg-opacity-60 rounded-full hover:bg-opacity-90"
                          onClick={() => removeSelectedImage(data)}
                          style={{ lineHeight: 1 }}
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                          >
                            <path
                              d="M4 4L12 12"
                              stroke="gray"
                              strokeWidth="2"
                              strokeLinecap="round"
                            />
                            <path
                              d="M12 4L4 12"
                              stroke="gray"
                              strokeWidth="2"
                              strokeLinecap="round"
                            />
                          </svg>
                        </button>
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        </div>
      </form>
      <div className="w-full px-5 pb-6 mb-20 bg-[#F5F5F5] flex gap-2">
        {editMode ? (
          <Button
            className="w-full bg-[#F5F5F5]"
            style={{ pointerEvents: "auto" }}
            onClick={handleSubmit((data) => bindSubmit(data, false))}
            disabled={!isValid || isSubmitting}
          >
            <div className="py-3 mx-auto w-full font-semibold text-center text-white bg-orange-400 rounded-full text-[16px] transition-all duration-300 hover:bg-orange-500 transform hover:scale-[1.02]">
              {isSubmitting ? "수정 중..." : "수정 완료"}
            </div>
          </Button>
        ) : (
          <>
            <Button
              className="w-full bg-[#F5F5F5]"
              style={{ pointerEvents: "auto" }}
              onClick={handleSubmit((data) => bindSubmit(data, true))}
              disabled={!isValid || isSubmitting}
            >
              <div className="py-3 mx-auto w-full font-semibold text-center text-yellow-500 bg-white rounded-full border border-yellow text-[16px] text-yellow transition-all duration-300 hover:bg-yellow-50 hover:border-yellow-400 transform hover:scale-[1.02]">
                저장하기
              </div>
            </Button>
            <Button
              className="w-full bg-[#F5F5F5]"
              style={{ pointerEvents: "auto" }}
              onClick={handleSubmit((data) => bindSubmit(data, false))}
              disabled={!isValid || isSubmitting}
            >
              <div className="py-3 mx-auto w-full font-semibold text-center text-white bg-orange-400 rounded-full text-[16px] transition-all duration-300 hover:bg-orange-500 transform hover:scale-[1.02]">
                즉시 발송
              </div>
            </Button>
          </>
        )}
      </div>
      {/* 모달 영역 */}
      {/* 달력 모달 */}
      <MessageModal
        isOpen={isOpenModal}
        onRequestClose={closeModal}
        className="pt-0 pb-6 mx-auto w-full max-w-sm bg-white rounded-2xl border-2 border-gray-200 shadow-lg outline-none"
        overlayClassName="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50"
        shouldCloseOnOverlayClick={false}
      >
        <CalendarComponent
          disabled={false}
          disablePast={true}
          dayOnClick={(day) => {
            setSelectedDate(day);
          }}
          selectedDay={selectedDate}
          schedules={{}}
        />
        <div className="flex gap-3 mt-2">
          <button
            className="flex-1 py-2 font-semibold text-gray-700 bg-gray-200 rounded-full border border-gray-300"
            onClick={closeModal}
          >
            취소
          </button>
          <button
            className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-full"
            onClick={closeModal}
          >
            확인
          </button>
        </div>
      </MessageModal>

      {/* 달력 모달 */}
      <MessageModal
        isOpen={isOpenImageModal}
        onRequestClose={closeImageModal}
        className="pt-0 pb-6 mx-auto w-full max-w-sm bg-white rounded-2xl border-2 border-gray-200 shadow-lg outline-none"
        overlayClassName="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50"
        shouldCloseOnOverlayClick={false}
      >
        <div className="flex flex-col gap-2 overflow-y-auto h-[500px]">
          {albumData.length === 0 ? (
            <div className="flex flex-col flex-1 gap-1 justify-center items-center px-6 text-center">
              <p className="text-gray-600">사진첩에 등록된 사진이 없습니다.</p>
              <p className="text-sm text-gray-400">
                사진첩에서 먼저 사진을 업로드해주세요.
              </p>
            </div>
          ) : (
          <div className="grid grid-cols-4 gap-1 sm:grid-cols-4">
            {albumData.map(
              (data) => {
                return (
                  <div
                    key={data.ImgID}
                    className={`relative cursor-pointer aspect-square border-2 rounded-lg ${
                      modalSelectedImages?.find(
                        (item) => item.ImgID === data.ImgID
                      )
                        ? " border-orange-500"
                        : "border-white bg-white"
                    }`}
                    onClick={() => toggleImageSelection(data)}
                  >
                    <AttachmentPreview
                      fileSeq={data.ImgID}
                      originalUrl={data.ImgURL}
                      thumbnailUrl={data.thumbnailUrl}
                      thumbnailStatus={data.thumbnailStatus}
                    />
                  </div>
                );
              }
            )}
          </div>
          )}
        </div>
        <div className="flex gap-3 mt-2">
          <button
            className="flex-1 py-2 font-semibold text-gray-700 bg-gray-200 rounded-full border border-gray-300"
            onClick={closeImageModal}
          >
            취소
          </button>
          <button
            className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-full"
            onClick={handleImgModal}
          >
            선택
          </button>
        </div>
      </MessageModal>

      {/* 성공 다이얼로그 */}
      <Dialog
        visible={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
      >
        <div className="text-center">
          <h3 className="mb-2 text-lg font-semibold">공지사항 발송 완료</h3>
          <p className="mb-4 text-gray-600">
            공지사항이 성공적으로 발송되었습니다.
          </p>
          <button
            onClick={() => {
              setShowSuccessDialog(false);
              router.navigate({ to: "/business/layout/notify" });
            }}
            className="px-4 py-2 text-white bg-orange-400 rounded-lg hover:bg-orange-500"
          >
            확인
          </button>
        </div>
      </Dialog>
    </>
  );
};

export default SendSavedNotify;
